import { Injectable, ElementRef, DestroyRef, inject } from '@angular/core';

import { HelpConfig, HelpAnchor, HelpMode } from './help.models';

import { BehaviorSubject, Subject } from 'rxjs';

// * Handles anchor registration, state management, and orchestration
@Injectable({
  providedIn: 'root',
})
export class HelpService {
  private destroyRef = inject(DestroyRef);

  // * Counter for generating unique anchor IDs
  private anchorIdCounter = 0;

  private anchors = new Map<string, HelpAnchor>();

  // * Currently active popover anchor ID
  private activePopoverIdSubject = new BehaviorSubject<string | null>(null);
  readonly activePopoverId$ = this.activePopoverIdSubject.asObservable();

  // * Currently active panel anchor ID
  private activePanelIdSubject = new BehaviorSubject<string | null>(null);
  readonly activePanelId$ = this.activePanelIdSubject.asObservable();

  // * Active inline help IDs (can have multiple, no observable needed currently)
  private activeInlineIdsSubject = new BehaviorSubject<Set<string>>(new Set());

  constructor() {
    this.setupEscapeHandler();
  }

  // * Sets up ESCAPE key handling in capture phase.
  // * this intercepts ESCAPE before ng-primitives dialog sees it,
  // * allowing help to close without closing the parent dialog.
  private setupEscapeHandler(): void {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      // * check DOM directly
      const hasActivePopover = !!document.querySelector('[data-help-popover]');
      const hasActivePanel = !!this.activePanelIdSubject.value;

      // ! if any help is active, prevent ALL other listeners from running
      if (hasActivePopover || hasActivePanel) {
        event.stopImmediatePropagation();
        event.preventDefault();

        if (hasActivePopover) {
          this.closePopover();
        }
        if (hasActivePanel) {
          this.closePanel();
        }
      }
    };

    // * listen in capture phase with HIGHEST priority
    // * this must run before ng-primitives popover/dialog listeners
    document.addEventListener('keydown', handler, { capture: true });

    // * cleanup on service destroy
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('keydown', handler, { capture: true });
    });
  }

  registerAnchor(element: ElementRef<HTMLElement>, config: HelpConfig): string {
    const id = this.generateAnchorId();
    const anchor: HelpAnchor = {
      id,
      element,
      config,
    };

    this.anchors.set(id, anchor);

    return id;
  }

  unregisterAnchor(anchorId: string): void {
    // Close any active help associated with this anchor
    if (this.activePopoverIdSubject.value === anchorId) {
      this.closePopover();
    }
    if (this.activePanelIdSubject.value === anchorId) {
      this.closePanel();
    }

    const activeInlineIds = this.activeInlineIdsSubject.value;
    if (activeInlineIds.has(anchorId)) {
      this.closeInline(anchorId);
    }

    this.anchors.delete(anchorId);
  }

  getAnchor(anchorId: string): HelpAnchor | undefined {
    return this.anchors.get(anchorId);
  }

  getAllAnchors(): HelpAnchor[] {
    return Array.from(this.anchors.values());
  }

  openHelp(anchorId: string, mode?: HelpMode): void {
    const anchor = this.anchors.get(anchorId);
    if (!anchor) {
      console.warn(`Cannot open help: anchor ${anchorId} not found`);
      return;
    }

    const helpMode = mode || anchor.config.mode;

    switch (helpMode) {
      case 'popover':
        this.openPopover(anchorId);
        break;
      case 'panel':
        this.openPanel(anchorId);
        break;
      case 'inline':
        this.openInline(anchorId);
        break;
    }
  }

  // * Popover ::
  openPopover(anchorId: string): void {
    // ! only one active help instance at a time
    if (this.activePanelIdSubject.value) {
      this.closePanel();
    }

    if (this.activePopoverIdSubject.value) {
      this.closePopover();
    }

    this.activePopoverIdSubject.next(anchorId);
  }

  closePopover(): void {
    this.activePopoverIdSubject.next(null);
  }

  getActivePopoverId(): string | null {
    return this.activePopoverIdSubject.value;
  }

  // * Panel ::
  openPanel(anchorId: string): void {
    // ! only one active help instance at a time
    if (this.activePopoverIdSubject.value) {
      this.closePopover();
    }

    if (this.activePanelIdSubject.value) {
      this.closePanel();
    }

    this.activePanelIdSubject.next(anchorId);
  }

  closePanel(): void {
    this.activePanelIdSubject.next(null);
  }

  getActivePanelId(): string | null {
    return this.activePanelIdSubject.value;
  }

  // * Inline ::
  openInline(anchorId: string): void {
    const activeIds = new Set(this.activeInlineIdsSubject.value);
    activeIds.add(anchorId);
    this.activeInlineIdsSubject.next(activeIds);
  }

  closeInline(anchorId: string): void {
    const activeIds = new Set(this.activeInlineIdsSubject.value);
    activeIds.delete(anchorId);
    this.activeInlineIdsSubject.next(activeIds);
  }

  closeAll(): void {
    this.closePopover();
    this.closePanel();
    this.activeInlineIdsSubject.next(new Set());
  }

  private generateAnchorId(): string {
    return `help-anchor-${++this.anchorIdCounter}`;
  }
}
